import React, { useState } from 'react';
import FormService from '../services/FormService';
import AnalyticsService from '../services/AnalyticsService';
import './ContactPage.css';
import ProjectStartForm from './ProjectStartForm';

const ContactPage = () => {
  const [topic, setTopic] = useState('general'); // 'general', 'urban_renewal', 'collaboration'
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const handleTopicChange = (newTopic) => {
    setTopic(newTopic);
    setSubmitted(false); // Reset submitted state when switching forms
    setErrors({});
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'נא למלא שם מלא';
    if (formData.phone.trim() && !/^[0-9]{9,15}$/.test(formData.phone.replace(/[-\s]/g, ''))) {
      newErrors.phone = 'מספר טלפון לא תקין';
    }
    
    if (!formData.email.trim()) newErrors.email = 'נא למלא כתובת אימייל';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'כתובת אימייל לא תקינה';
    
    if (!formData.message.trim()) newErrors.message = 'נא לכתוב את תוכן ההודעה';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      // Fire and forget - don't wait for response
      FormService.submitForm('Contact Page Form', { topic, ...formData })
        .catch(error => console.error('Error submitting contact form (background):', error));
      AnalyticsService.trackContactSubmitted({
        sourceSurface: 'contact_page',
        topic,
        address: formData.address,
      });
      setSubmitted(true);
      // Scroll to top to show success message and prevent header glitches
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // If Urban Renewal is selected, show the specialized form
  if (topic === 'urban_renewal') {
    return (
      <div className="contact-page">
        <div className="topic-tabs">
          <button 
            className={`topic-tab ${topic === 'general' ? 'active' : ''}`}
            onClick={() => handleTopicChange('general')}
          >
            פנייה כללית
          </button>
          <button 
            className={`topic-tab ${topic === 'urban_renewal' ? 'active' : ''}`}
            onClick={() => handleTopicChange('urban_renewal')}
          >
            התחדשות עירונית
          </button>
          <button 
            className={`topic-tab ${topic === 'collaboration' ? 'active' : ''}`}
            onClick={() => handleTopicChange('collaboration')}
          >
            שיתוף פעולה עסקי
          </button>
        </div>
        
        {/* Reuse existing ProjectStartForm */}
        <div className="urban-renewal-form-wrapper">
          <ProjectStartForm startOpen={true} origin="Contact Us Page" />
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="contact-page">
        <div className="topic-tabs">
          <button 
            className={`topic-tab ${topic === 'general' ? 'active' : ''}`}
            onClick={() => handleTopicChange('general')}
          >
            פנייה כללית
          </button>
          <button 
            className={`topic-tab ${topic === 'urban_renewal' ? 'active' : ''}`}
            onClick={() => handleTopicChange('urban_renewal')}
          >
            התחדשות עירונית
          </button>
          <button 
            className={`topic-tab ${topic === 'collaboration' ? 'active' : ''}`}
            onClick={() => handleTopicChange('collaboration')}
          >
            שיתוף פעולה עסקי
          </button>
        </div>

        <div className="contact-card success">
          <div className="success-icon"><i className="fi fi-rr-envelope"></i></div>
          <h2>ההודעה נשלחה בהצלחה!</h2>
          <p>קיבלנו את הפנייה שלך ונחזור אליך בהקדם האפשרי.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="contact-page">
      <div className="topic-tabs">
        <button 
          className={`topic-tab ${topic === 'general' ? 'active' : ''}`}
          onClick={() => handleTopicChange('general')}
        >
          פנייה כללית
        </button>
        <button 
          className={`topic-tab ${topic === 'urban_renewal' ? 'active' : ''}`}
          onClick={() => handleTopicChange('urban_renewal')}
        >
          התחדשות עירונית
        </button>
        <button 
          className={`topic-tab ${topic === 'collaboration' ? 'active' : ''}`}
          onClick={() => handleTopicChange('collaboration')}
        >
          שיתוף פעולה עסקי
        </button>
      </div>

      <div className="contact-card">
        <div className="contact-header">
          <h2>{topic === 'collaboration' ? 'יצירת קשר עסקי' : 'צור קשר'}</h2>
          <p>
            {topic === 'collaboration' 
              ? 'מעוניינים לשתף פעולה? נשמח לשמוע על ההזדמנות.'
              : 'יש לכם שאלה? צריכים עזרה? אנחנו כאן בשבילכם.'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="contact-form" noValidate>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="name" className="form-label">שם מלא</label>
              <input
                type="text"
                id="name"
                name="name"
                autoComplete="name"
                value={formData.name}
                onChange={handleChange}
                className={`contact-input ${errors.name ? 'input-error' : ''}`}
                placeholder="שם מלא"
              />
              {errors.name && <span className="error-message">{errors.name}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="phone" className="form-label">טלפון (אופציונלי)</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                autoComplete="tel"
                value={formData.phone}
                onChange={handleChange}
                className={`contact-input ${errors.phone ? 'input-error' : ''}`}
                placeholder="050-0000000"
                dir="ltr"
              />
              {errors.phone && <span className="error-message">{errors.phone}</span>}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="email" className="form-label">אימייל</label>
            <input
              type="email"
              id="email"
              name="email"
              autoComplete="email"
              value={formData.email}
              onChange={handleChange}
              className={`contact-input ${errors.email ? 'input-error' : ''}`}
              placeholder="your@email.com"
              dir="ltr"
            />
            {errors.email && <span className="error-message">{errors.email}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="message" className="form-label">תוכן ההודעה</label>
            <textarea
              id="message"
              name="message"
              autoComplete="off"
              value={formData.message}
              onChange={handleChange}
              rows={5}
              className={`contact-textarea ${errors.message ? 'input-error' : ''}`}
              placeholder="כיצד נוכל לעזור?"
            />
            {errors.message && <span className="error-message">{errors.message}</span>}
          </div>

          <button type="submit" className="btn btn-primary btn-lg btn-full">
            שליחת הודעה
          </button>
        </form>
      </div>
    </div>
  );
};

export default ContactPage;
